// src/components/chat/conversation/listen-button/listen-button.tsx
import * as React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  ListenControlWrap,
  CollapsedBtn,
  SeekerWrap,
  PauseBtn,
  PauseIcon,
  Track,
  Fill,
  DurationText,
} from './listen-button.styled';

export type ListenButtonProps = {
  onStart: () => void;
  onTogglePause: () => void;
  onSeek: (pct: number) => void;

  loading?: boolean;
  playing?: boolean;
  paused?: boolean;
  hasAudio?: boolean;

  durationMs?: number;
  progressPct?: number;
  /** Direct elapsed playback time in ms — used when duration is unknown (streaming audio). */
  elapsedMs?: number;

  disabled?: boolean;
  spinner?: React.ReactNode;

  className?: string;
  title?: string;

  /** Only affects animation/layout origin (NOT seek/progress direction) */
  direction?: 'ltr' | 'rtl';
};

function formatDuration(ms?: number): string {
  const m = Number(ms ?? 0);
  if (!Number.isFinite(m) || m < 0) return '0:00';
  const totalSec = Math.max(0, Math.floor(m / 1000));
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return `${min}:${String(sec).padStart(2, '0')}`;
}

function clamp01(n: number) {
  return Math.max(0, Math.min(1, n));
}

export const ConversationListenButton: React.FC<ListenButtonProps> = ({
  onStart,
  onTogglePause,
  onSeek,
  loading = false,
  playing = false,
  paused = false,
  hasAudio = false,
  durationMs = 0,
  progressPct,
  elapsedMs: elapsedMsProp,
  disabled,
  spinner,
  className,
  title,
  direction = 'ltr',
}) => {
  const isDisabled = Boolean(disabled || loading || !hasAudio);
  const expanded = Boolean((playing || paused) && hasAudio);

  const pctNum = Number.isFinite(progressPct as number) ? clamp01(Number(progressPct)) : 0;

  // Prefer direct elapsed ms (tracked from a.currentTime) over the pct-derived estimate.
  // This keeps elapsed accurate even when total duration is unknown (streaming audio).
  const elapsedMsComputed =
    typeof elapsedMsProp === 'number' && elapsedMsProp >= 0
      ? elapsedMsProp
      : Math.max(0, Math.round(pctNum * Math.max(0, durationMs)));

  const elapsedLabel = formatDuration(elapsedMsComputed);
  const totalLabel = formatDuration(durationMs);
  // When total is unknown (0), show only elapsed so we don't display "0:23 / 0:00"
  const durationLabel = durationMs > 0 ? `${elapsedLabel} / ${totalLabel}` : elapsedLabel;

  // NOTE: layout origin only (progress/seek is always LTR)
  const originX = direction === 'rtl' ? 1 : 0;

  const trackRef = React.useRef<HTMLDivElement | null>(null);

  // ✅ Mobile-stable drag: lock pointer id + RAF-throttle seek updates
  const draggingRef = React.useRef(false);
  const activePointerIdRef = React.useRef<number | null>(null);
  const rafSeekRef = React.useRef<number>(0);
  const pendingPctRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    return () => {
      if (rafSeekRef.current) cancelAnimationFrame(rafSeekRef.current);
    };
  }, []);

  // ✅ Seek is ALWAYS left-to-right (matches desired progress direction)
  const pctFromClientX = React.useCallback((clientX: number) => {
    const el = trackRef.current;
    if (!el) return 0;
    const rect = el.getBoundingClientRect();
    const x = clientX - rect.left;
    const raw = rect.width > 0 ? x / rect.width : 0;
    return clamp01(raw);
  }, []);

  const flushSeek = React.useCallback(() => {
    rafSeekRef.current = 0;
    const pct = pendingPctRef.current;
    pendingPctRef.current = null;
    if (pct == null) return;
    onSeek(pct);
  }, [onSeek]);

  const scheduleSeek = React.useCallback(
    (pct: number) => {
      pendingPctRef.current = pct;
      if (rafSeekRef.current) return;
      rafSeekRef.current = requestAnimationFrame(flushSeek);
    },
    [flushSeek]
  );

  const setSeekFromClientX = React.useCallback(
    (clientX: number) => {
      scheduleSeek(pctFromClientX(clientX));
    },
    [pctFromClientX, scheduleSeek]
  );

  const onTrackPointerDown = (e: React.PointerEvent) => {
    if (isDisabled) return;

    draggingRef.current = true;
    activePointerIdRef.current = e.pointerId;

    e.preventDefault();

    try {
      (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
    } catch {
      // ignore
    }

    setSeekFromClientX(e.clientX);
  };

  const onTrackPointerMove = (e: React.PointerEvent) => {
    if (isDisabled) return;
    if (!draggingRef.current) return;
    if (activePointerIdRef.current != null && e.pointerId !== activePointerIdRef.current) return;

    e.preventDefault();
    setSeekFromClientX(e.clientX);
  };

  const endDrag = (e: React.PointerEvent) => {
    if (activePointerIdRef.current != null && e.pointerId !== activePointerIdRef.current) return;

    draggingRef.current = false;
    activePointerIdRef.current = null;

    try {
      (e.currentTarget as HTMLDivElement).releasePointerCapture(e.pointerId);
    } catch {
      // ignore
    }
  };

  return (
    <ListenControlWrap className={className}>
      <AnimatePresence mode="wait" initial={false}>
        {!expanded ? (
          <motion.div
            key="collapsed"
            style={{
              transformOrigin: direction === 'rtl' ? '100% 50%' : '0% 50%',
              width: 'auto',
              minWidth: 0,
              maxWidth: '100%',
              flex: '0 1 auto',
              display: 'inline-flex',
            }}
            initial={{ opacity: 0, scale: 0.985, y: 1 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.992, y: 1 }}
            transition={{ duration: 0.26, ease: [0.22, 1, 0.36, 1] }}
          >
            <CollapsedBtn
              type="button"
              disabled={isDisabled}
              aria-busy={loading || undefined}
              onClick={onStart}
              title={title ?? 'Listen'}
              onMouseDown={(e) => e.preventDefault()}
            >
              {loading ? (
                (spinner ?? <span aria-hidden="true" />)
              ) : (
                <img src="/icons/onboarding/question/resume.svg" alt="" aria-hidden="true" />
              )}
              <span>Listen</span>
            </CollapsedBtn>
          </motion.div>
        ) : (
          <motion.div
            key="expanded"
            style={{
              transformOrigin: `${originX * 100}% 50%`,
              width: 'auto',
              minWidth: 0,
              maxWidth: '100%',
              flex: '0 1 auto',
            }}
            initial={{ opacity: 0, scale: 0.99, y: 1 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.99, y: 1 }}
            transition={{
              type: 'spring',
              stiffness: 320,
              damping: 34,
              mass: 1.2,
            }}
          >
            <SeekerWrap role="group" aria-label={title ?? (playing ? 'Pause' : 'Play')}>
              <PauseBtn
                type="button"
                onClick={onTogglePause}
                disabled={isDisabled}
                title={playing ? 'Pause' : 'Play'}
                onMouseDown={(e) => e.preventDefault()}
              >
                <PauseIcon
                  src={playing ? '/pause-chat.svg' : '/icons/onboarding/question/resume.svg'}
                  alt=""
                  aria-hidden="true"
                />
              </PauseBtn>

              {/* Invisible touch-target wrapper — increases tap area without changing visuals */}
              <div
                style={{ padding: '14px 0', margin: '-14px 0', cursor: 'pointer', touchAction: 'none', width: '100%' }}
                onPointerDown={onTrackPointerDown}
                onPointerMove={onTrackPointerMove}
                onPointerUp={endDrag}
                onPointerCancel={endDrag}
                onLostPointerCapture={() => {
                  draggingRef.current = false;
                  activePointerIdRef.current = null;
                }}
              >
                <Track
                  ref={trackRef}
                  aria-label="Seek"
                  role="slider"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={Math.round(pctNum * 100)}
                  tabIndex={isDisabled ? -1 : 0}
                  onKeyDown={(e) => {
                    if (isDisabled) return;
                    const step = 0.05;
                    if (e.key === 'ArrowLeft') onSeek(clamp01(pctNum - step));
                    if (e.key === 'ArrowRight') onSeek(clamp01(pctNum + step));
                  }}
                >
                  <Fill style={{ width: `${pctNum * 100}%` }} />
                </Track>
              </div>

              <DurationText>{durationLabel}</DurationText>
            </SeekerWrap>
          </motion.div>
        )}
      </AnimatePresence>
    </ListenControlWrap>
  );
};
